import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Brain, Plus, PencilSimple, Trash, UsersThree, Storefront,
} from "@phosphor-icons/react";

const EMPTY = { name: "", channels_min: "", channels_max: "", price: "" };

function toMinor(v) {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function toDollars(m) {
  if (m == null) return "";
  return (Number(m) / 100).toFixed(2);
}

function PlanTable({ scope, items, loading, canEdit, onAdd, onEdit, onDelete, busyId }) {
  const emptyTitle = scope === "customer" ? "No customer plans" : "No reseller plans";
  return (
    <div className="space-y-4" data-testid={`ai-pricing-${scope}`}>
      <div className="flex justify-end">
        {canEdit && (
          <Button
            type="button"
            className="rounded-lg"
            onClick={onAdd}
            data-testid={`ai-pricing-add-${scope}`}
          >
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add plan
          </Button>
        )}
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          testid={`ai-pricing-empty-${scope}`}
          icon={Brain}
          title={emptyTitle}
          description="Add a plan with a name, channel range, and price."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan name</TableHead>
                <TableHead>Channels range</TableHead>
                <TableHead>Price</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`ai-plan-row-${row.id}`}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="tabular-nums">
                    {row.channels_min} – {row.channels_max}
                  </TableCell>
                  <TableCell className="tabular-nums font-medium">
                    {money(row.price_minor)}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-lg h-8 w-8 p-0"
                          onClick={() => onEdit(row)}
                          data-testid={`ai-plan-edit-${row.id}`}
                          title="Edit"
                        >
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive"
                          disabled={busyId === row.id}
                          onClick={() => onDelete(row)}
                          data-testid={`ai-plan-delete-${row.id}`}
                          title="Delete"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AiPricing() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || can(user, "billing-pricing", "edit");
  const [tab, setTab] = useState("customer");
  const [customerItems, setCustomerItems] = useState([]);
  const [resellerItems, setResellerItems] = useState([]);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [loadingReseller, setLoadingReseller] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadScope = useCallback(async (scope) => {
    const setLoading = scope === "customer" ? setLoadingCustomer : setLoadingReseller;
    const setItems = scope === "customer" ? setCustomerItems : setResellerItems;
    setLoading(true);
    try {
      const { data } = await api.get("/billing/ai-pricing", { params: { scope } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScope("customer");
    loadScope("reseller");
  }, [loadScope]);

  const openAdd = (scope) => {
    setEdit(null);
    setForm({ ...EMPTY, _scope: scope });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({
      name: row.name || "",
      channels_min: String(row.channels_min ?? ""),
      channels_max: String(row.channels_max ?? ""),
      price: toDollars(row.price_minor),
      _scope: row.scope,
    });
    setOpen(true);
  };

  const save = async () => {
    const scope = edit?.scope || form._scope || tab;
    const name = (form.name || "").trim();
    const lo = parseInt(form.channels_min, 10);
    const hi = parseInt(form.channels_max, 10);
    const priceMinor = toMinor(form.price);
    if (!name) { toast.error("Plan name is required"); return; }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      toast.error("Enter a valid channels range"); return;
    }
    if (lo > hi) {
      toast.error("Channels minimum cannot be greater than maximum"); return;
    }
    if (priceMinor == null) { toast.error("Enter a valid price"); return; }

    setSaving(true);
    try {
      const body = {
        name,
        channels_min: lo,
        channels_max: hi,
        price_minor: priceMinor,
      };
      if (edit) {
        await api.patch(`/billing/ai-pricing/${edit.id}`, body);
        toast.success("Plan updated");
      } else {
        await api.post("/billing/ai-pricing", body, { params: { scope } });
        toast.success("Plan added");
      }
      setOpen(false);
      loadScope(scope);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete plan “${row.name}”?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/billing/ai-pricing/${row.id}`);
      toast.success("Plan deleted");
      loadScope(row.scope);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-pricing-page">
      <PageHeader
        title="AI Pricing"
        subtitle="Channel-range plans for AI engine call limits — separate catalogs for customers and resellers."
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="ai-pricing-tabs">
          <TabsTrigger value="customer" data-testid="ai-pricing-tab-customer">
            <UsersThree className="w-4 h-4 mr-1.5" weight="duotone" /> Customer Pricing
          </TabsTrigger>
          <TabsTrigger value="reseller" data-testid="ai-pricing-tab-reseller">
            <Storefront className="w-4 h-4 mr-1.5" weight="duotone" /> Reseller Pricing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customer">
          <PlanTable
            scope="customer"
            items={customerItems}
            loading={loadingCustomer}
            canEdit={canEdit}
            onAdd={() => openAdd("customer")}
            onEdit={openEdit}
            onDelete={remove}
            busyId={busyId}
          />
        </TabsContent>
        <TabsContent value="reseller">
          <PlanTable
            scope="reseller"
            items={resellerItems}
            loading={loadingReseller}
            canEdit={canEdit}
            onAdd={() => openAdd("reseller")}
            onEdit={openEdit}
            onDelete={remove}
            busyId={busyId}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ai-plan-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit plan" : "Add plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="pp-name">Plan name</Label>
              <Input
                id="pp-name"
                data-testid="pp-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Starter"
                maxLength={120}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="pp-min">Channels from</Label>
                <Input
                  id="pp-min"
                  data-testid="pp-channels-min"
                  type="number"
                  min="0"
                  step="1"
                  value={form.channels_min}
                  onChange={(e) => setForm((f) => ({ ...f, channels_min: e.target.value }))}
                  placeholder="1"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pp-max">Channels to</Label>
                <Input
                  id="pp-max"
                  data-testid="pp-channels-max"
                  type="number"
                  min="0"
                  step="1"
                  value={form.channels_max}
                  onChange={(e) => setForm((f) => ({ ...f, channels_max: e.target.value }))}
                  placeholder="10"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pp-price">Price (USD)</Label>
              <div className="relative max-w-[220px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  id="pp-price"
                  data-testid="pp-price"
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-7"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              disabled={saving}
              onClick={save}
              data-testid="pp-save"
            >
              {saving ? "Saving…" : edit ? "Save changes" : "Add plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
